import { CSSProperties } from 'react';
import { Link } from '@react-email/components';
import { FooterSection } from '@weave/schema-gen-ts/dist/schemas/messaging/shared/v1/composer.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { useComposerSection, useLinks } from '../../hooks';
import { ComposerSectionOutputProps, FooterLinkType, FrameAlignment } from '../../types';
import { EmailSvg } from '../assets/email-svg';
import { FacebookSvg } from '../assets/facebook-svg';
import { GoogleSvg } from '../assets/google-svg';
import { PhoneSvg } from '../assets/phone-svg';
import { useContentComposer } from '../content-composer-provider';
import { EmailTable, getAlignment, SectionFrame } from '../sections/image-elements';

export const FooterOutput = ({ renderMode }: ComposerSectionOutputProps) => {
  const { t } = useTranslation('content-composer');
  const { props } = useComposerSection<FooterSection>();
  const { composerMode } = useContentComposer();
  const isPreview = renderMode === 'preview';

  const { address, businessName, formatLink, managementUrl, oneClickUrl } = useLinks();

  const alignment = getAlignment(props.frame?.alignment);

  const textStyle = {
    color: props.textColor,
    fontSize: props.textSize,
    fontStyle: props.italic ? 'italic' : 'normal',
    fontFamily: props.fontType,
    fontWeight: props.bold ? theme.font.weight.bold : theme.font.weight.regular,
  };

  return (
    <SectionFrame {...props.frame}>
      <EmailTable>
        {props?.links && props.links.length > 0 && (
          <Line alignment={alignment} style={{ paddingBottom: theme.spacing(4) }}>
            {props.links.map((link, index, arr) => {
              const marginRight = index === arr.length - 1 ? 0 : theme.spacing(1);
              return (
                <Link
                  key={link.url + '-' + index}
                  href={formatLink(link.url, link.linkType as FooterLinkType, renderMode)}
                  style={{
                    ...textStyle,
                    verticalAlign: link.linkType === 'custom' ? 'super' : 'initial',
                    marginRight,
                    width: link.linkType !== 'custom' ? theme.spacing(3) : 'auto',
                    height: link.linkType !== 'custom' ? theme.spacing(3) : 'auto',
                  }}
                >
                  {link.linkType === 'custom' && link?.text}
                  {link.linkType === 'email' && <EmailSvg fillColor={props.textColor} />}
                  {link.linkType === 'phone' && <PhoneSvg fillColor={props.textColor} />}
                  {link.linkType === 'facebook' && <FacebookSvg fillColor={props.textColor} />}
                  {link.linkType === 'google' && <GoogleSvg fillColor={props.textColor} />}
                </Link>
              );
            })}
          </Line>
        )}
        <Line alignment={alignment} style={textStyle}>
          {isPreview ? address : '{{BUSINESS_GROUP_ADDRESS}}'}
        </Line>
        <Line alignment={alignment} style={{ ...textStyle, paddingBottom: theme.spacing(4) }}>
          {isPreview ? businessName : '{{BUSINESS_GROUP_NAME}}'}
        </Line>
        {composerMode !== 'feedback' && (
          <Line alignment={alignment}>
            <EmailTable alignment={alignment} fullWidth={false} style={{ display: 'inline-table' }}>
              <EmailTable.Row>
                <EmailTable.Cell alignment={alignment}>
                  <Link href={isPreview ? managementUrl : '{{PREFERENCE_MANAGEMENT_LINK}}'} style={textStyle}>
                    {t('Preference Center')}
                  </Link>
                </EmailTable.Cell>
                <EmailTable.Cell
                  alignment={alignment}
                  style={{ ...textStyle, fontSize: '14px', padding: theme.spacing(0, 1), color: props.textColor }}
                >
                  |
                </EmailTable.Cell>
                <EmailTable.Cell alignment={alignment}>
                  <Link href={isPreview ? oneClickUrl : '{{UNSUBSCRIBE_LINK}}'} style={textStyle}>
                    {t('Unsubscribe')}
                  </Link>
                </EmailTable.Cell>
              </EmailTable.Row>
            </EmailTable>
          </Line>
        )}
      </EmailTable>
    </SectionFrame>
  );
};

type LineProps = {
  alignment?: FrameAlignment;
  children: React.ReactNode;
  style?: CSSProperties;
};

const Line = ({ alignment = 'center', children, style }: LineProps) => (
  <EmailTable.Row>
    <EmailTable.Cell alignment={alignment} style={{ paddingBottom: theme.spacing(1), ...style }}>
      {children}
    </EmailTable.Cell>
  </EmailTable.Row>
);
